// Logica pura dell'editor Sala e tavoli (testabile senza React).

export const ROOM_W = 840;
export const ROOM_H = 560;
export const GRID = 20; // quadretti della pianta
export const SNAP = 10; // aggancio durante il trascinamento
export const SEATS = [1, 30];
export const SIZE = [40, 400];
export const ROTATIONS = [0, 45, 90, 135];
export const CHAIR = 16; // lato della sedia
export const CHAIR_GAP = 4; // distanza della sedia dal bordo del tavolo

export const roomSize = (room) => ({ w: room?.width || ROOM_W, h: room?.height || ROOM_H });

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
export const snap = (v, step = SNAP) => Math.round(v / step) * step;

// Misure predefinite per forma: Quadrato 80x80, Rettangolare 140x80 (180x80 da 6 posti), Rotondo ø 90
export const defaultSize = (shape, seatsMax = 4) => {
  if (shape === "round") return { width: 90, height: 90 };
  if (shape === "rect") return { width: seatsMax >= 6 ? 180 : 140, height: 80 };
  return { width: 80, height: 80 };
};

// Ingombro reale di un tavolo ruotato (la rotazione CSS è attorno al centro)
export const footprint = (tb) => {
  const a = ((tb.rotation || 0) * Math.PI) / 180;
  const w = tb.width || 80;
  const h = tb.height || 80;
  if (tb.shape === "round") return { w, h };
  return {
    w: Math.abs(w * Math.cos(a)) + Math.abs(h * Math.sin(a)),
    h: Math.abs(w * Math.sin(a)) + Math.abs(h * Math.cos(a)),
  };
};

// Posizione (x, y = angolo in alto a sinistra non ruotato) tenuta dentro la pianta
export const clampPos = (tb, x, y, room) => {
  const { w: rw, h: rh } = roomSize(room);
  const f = footprint(tb);
  const dx = (f.w - tb.width) / 2;
  const dy = (f.h - tb.height) / 2;
  // il backend non accetta coordinate negative: il minimo resta 0
  const lx = Math.max(0, dx);
  const ly = Math.max(0, dy);
  return {
    x: Math.round(clamp(x, lx, Math.max(lx, rw - tb.width - dx))),
    y: Math.round(clamp(y, ly, Math.max(ly, rh - tb.height - dy))),
  };
};

// Trascinamento: aggancio ogni 10 px, sempre dentro l'area
export const dragTo = (tb, x, y, room) => clampPos(tb, snap(x), snap(y), room);

// Spostamento con le frecce: 10 px (con Maiusc 1 px)
export const nudge = (tb, key, shift, room) => {
  const step = shift ? 1 : SNAP;
  const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[key];
  if (!d) return null;
  return clampPos(tb, (tb.x || 0) + d[0], (tb.y || 0) + d[1], room);
};

// Primo nome T<n> non usato in nessuna sala (confronto senza maiuscole)
export const nextTableName = (names) => {
  const used = new Set(names.map((n) => String(n).trim().toLowerCase()));
  let n = 1;
  while (used.has(`t${n}`)) n += 1;
  return `T${n}`;
};

// Nome libero per la copia: T6 -> prima T<n> successiva non usata, altrimenti "<nome> 2", "<nome> 3"...
export const copyName = (name, taken) => {
  const used = new Set(taken.map((n) => String(n).toLowerCase()));
  const m = String(name).match(/^(.*?)(\d+)$/);
  const stem = m ? m[1] : `${name} `;
  let n = m ? Number(m[2]) + 1 : 2;
  while (used.has(`${stem}${n}`.toLowerCase())) n += 1;
  return `${stem}${n}`.slice(0, 30);
};

const overlap = (a, b, margin) =>
  a.x < b.x + b.w + margin && b.x < a.x + a.w + margin && a.y < b.y + b.h + margin && b.y < a.y + a.h + margin;

// Rettangolo occupato (con le sedie) attorno al centro del tavolo
const box = (tb) => {
  const f = footprint(tb);
  const cx = (tb.x || 0) + (tb.width || 80) / 2;
  const cy = (tb.y || 0) + (tb.height || 80) / 2;
  const pad = CHAIR + CHAIR_GAP;
  return { x: cx - f.w / 2 - pad, y: cy - f.h / 2 - pad, w: f.w + 2 * pad, h: f.h + 2 * pad };
};

// Primo punto libero sulla griglia (righe dall'alto, da sinistra) per un tavolo di quelle misure
export const freeSpot = (tb, tables, room) => {
  const { w: rw, h: rh } = roomSize(room);
  const others = tables.map(box);
  const pad = CHAIR + CHAIR_GAP;
  const start = Math.ceil(pad / GRID) * GRID;
  for (let y = start; y + tb.height + pad <= rh; y += GRID) {
    for (let x = start; x + tb.width + pad <= rw; x += GRID) {
      const me = box({ ...tb, x, y });
      if (!others.some((o) => overlap(me, o, 0))) return clampPos(tb, x, y, room);
    }
  }
  return clampPos(tb, start, start, room); // pianta piena: in alto a sinistra
};

// Nuovo tavolo quadrato 1-4 posti, nome progressivo libero in tutte le sale, in un punto libero
export const newTable = (tables, allNames, room) => {
  const tb = { name: nextTableName(allNames), seats_min: 1, seats_max: 4, shape: "square", ...defaultSize("square"), rotation: 0, joinable: true, active: true };
  return { ...tb, ...freeSpot(tb, tables, room) };
};

// Copia di un tavolo: stesse caratteristiche, nome progressivo libero, in un punto libero; senza id
export const duplicateTable = (source, tables, allNames, room) => {
  const { id, key, ...rest } = source;
  const copy = { ...rest, name: copyName(source.name, allNames) };
  return { ...copy, ...freeSpot(copy, tables, room) };
};

// Nome valido: obbligatorio e unico in tutte le sale. Ritorna la chiave dell'errore o null.
export const nameError = (name, otherNames) => {
  const v = String(name || "").trim();
  if (!v) return "plan.nameRequired";
  if (otherNames.some((n) => String(n).trim().toLowerCase() === v.toLowerCase())) return "plan.nameTaken";
  return null;
};

const toInt = (raw) => {
  const v = Number(String(raw).replace(",", "."));
  return Number.isFinite(v) && String(raw).trim() !== "" ? v : null;
};

// Modifica di un campo numerico. `final` = uscita dal campo (Invio/blur): il valore si corregge.
// Durante la digitazione si applica solo un valore già valido. Ritorna la patch o null.
export const numberPatch = (tb, field, raw, final) => {
  const v = toInt(raw);
  if (field === "seats_min" || field === "seats_max") {
    if (v === null) return null;
    const n = Math.round(v);
    if (!final && (n !== v || n < SEATS[0] || n > SEATS[1])) return null;
    const val = clamp(n, SEATS[0], SEATS[1]);
    if (field === "seats_min") {
      if (!final && val > tb.seats_max) return null;
      return { seats_min: Math.min(val, tb.seats_max) };
    }
    if (!final && val < tb.seats_min) return null;
    const p = { seats_max: val, seats_min: Math.min(tb.seats_min, val) };
    // Rettangolare con misure predefinite: segue i posti (140 -> 180 da 6 posti)
    const before = defaultSize("rect", tb.seats_max);
    if (tb.shape === "rect" && tb.width === before.width && tb.height === before.height) {
      Object.assign(p, defaultSize("rect", val));
    }
    return p;
  }
  if (field === "width" || field === "height" || field === "diameter") {
    if (v === null) return null;
    if (!final && (v % 10 !== 0 || v < SIZE[0] || v > SIZE[1])) return null;
    const val = clamp(snap(v), SIZE[0], SIZE[1]);
    if (field === "diameter" || tb.shape === "round") return { width: val, height: val };
    return { [field]: val };
  }
  return null;
};

// Cambio forma: le misure tornano a quelle predefinite
export const shapePatch = (tb, shape) => ({ shape, ...defaultSize(shape, tb.seats_max), ...(shape === "round" ? { rotation: 0 } : {}) });

// Posizione delle sedie rispetto al centro del tavolo (sistema non ruotato): [{x, y, a}] con a = angolo in gradi.
// Rotondo: in cerchio. Quadrato: a turno sui quattro lati. Rettangolare: sui lati lunghi, più una per testata da 6 posti.
export const chairPositions = (tb) => {
  const n = Math.max(0, Math.min(SEATS[1], tb.seats_max || 0));
  const w = tb.width || 80;
  const h = tb.height || 80;
  const off = CHAIR / 2 + CHAIR_GAP;
  const out = [];
  if (!n) return out;
  if (tb.shape === "round") {
    const r = w / 2 + off;
    for (let i = 0; i < n; i += 1) {
      const a = -90 + (360 / n) * i;
      const rad = (a * Math.PI) / 180;
      out.push({ x: round2(r * Math.cos(rad)), y: round2(r * Math.sin(rad)), a: a + 90 });
    }
    return out;
  }
  // lati: top, right, bottom, left
  const sides = [0, 0, 0, 0];
  if (tb.shape === "rect") {
    const longH = w >= h; // lati lunghi = sopra/sotto
    const heads = n >= 6 ? 2 : 0;
    const rest = n - heads;
    const a = Math.ceil(rest / 2);
    const b = rest - a;
    if (longH) {
      sides[0] = a;
      sides[2] = b;
      sides[1] = heads / 2;
      sides[3] = heads / 2;
    } else {
      sides[1] = a;
      sides[3] = b;
      sides[0] = heads / 2;
      sides[2] = heads / 2;
    }
  } else {
    for (let i = 0; i < n; i += 1) sides[i % 4] += 1;
  }
  const spread = (k, len) => Array.from({ length: k }, (_, j) => round2(-len / 2 + (len * (j + 1)) / (k + 1)));
  spread(sides[0], w).forEach((x) => out.push({ x, y: -h / 2 - off, a: 0 }));
  spread(sides[1], h).forEach((y) => out.push({ x: w / 2 + off, y, a: 90 }));
  spread(sides[2], w).forEach((x) => out.push({ x: -x, y: h / 2 + off, a: 180 }));
  spread(sides[3], h).forEach((y) => out.push({ x: -w / 2 - off, y: -y, a: 270 }));
  return out;
};
const round2 = (v) => Math.round(v * 100) / 100 + 0; // +0 evita -0

// Riepilogo di una sala: tavoli in uso su totale e posti min-max dei tavoli in uso
export const roomSummary = (tables) => {
  const used = tables.filter((x) => x.active !== false);
  return {
    inUse: used.length,
    total: tables.length,
    seatsMin: used.reduce((s, x) => s + (Number(x.seats_min) || 0), 0),
    seatsMax: used.reduce((s, x) => s + (Number(x.seats_max) || 0), 0),
  };
};

// Coperti massimi per turno (Pranzo / Cena) dalle fasce attive: il massimo tra le fasce dello stesso tipo
export const coversByKind = (shifts = []) => {
  const out = {};
  shifts
    .filter((s) => s.active !== false)
    .forEach((s) => {
      const kind = s.kind || (String(s.start_time || "") < "17:00" ? "Pranzo" : "Cena");
      out[kind] = Math.max(out[kind] || 0, Number(s.capacity) || 0);
    });
  return out;
};
